import axiosInstance, { ApiKeys, getApiUrl, logError } from '@cp/src/utils/ApiHelper';
import { toast } from '@navi/web-ui/lib/primitives/Toast';
import { Dispatch } from '@reduxjs/toolkit';
import {
  GetQcAgentAuditsPayload,
  GetQcTeamLeadAuditsPayload,
  SubmitAuditPayload,
  SubmitQcTeamLeadAuditPayload
} from './interface';
import {
  setIsTableLoading,
  setCurrentPage,
  setPageSize,
  setTotalElements,
  setTotalPages,
  setQcAgentAudits,
  setCallingAgentAudits,
  setCallingAgentAuditDetails,
  updateCallingAgentAuditDetails,
  setQcAgentFilters,
  setQcTeamLeadFilters,
  setIsFeedbackSubmitting,
  setIsSidebarLoading
} from './callAuditSlice';

export const getQcAgentAudits =
  (payload: GetQcAgentAuditsPayload = {}) =>
  (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.QC_AGENT_AUDITS);
    const { page = 0, size = 10, auditStatusFilter } = payload;
    dispatch(setIsTableLoading(true));

    return axiosInstance
      .get(url, {
        params: {
          page,
          size,
          ...(auditStatusFilter?.length && { auditStatusFilter: auditStatusFilter.join(',') })
        },
        headers: { donotHandleError: true, includeInUptimeMetrics: true }
      })
      .then(res => {
        dispatch(setQcAgentAudits(res.data?.content || []));
        dispatch(setTotalElements(res.data?.totalElements || 0));
        dispatch(setTotalPages(res.data?.totalPages || 0));
        dispatch(setCurrentPage(page));
        dispatch(setPageSize(size));
        return res.data;
      })
      .catch(err => {
        logError('Error fetching QC agent audits', err);
        toast.error('Failed to fetch QC agent audits');
      })
      .finally(() => {
        dispatch(setIsTableLoading(false));
      });
  };

export const getQcTeamLeadAudits =
  (payload: GetQcTeamLeadAuditsPayload = {}) =>
  (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.QC_TEAM_LEAD_AUDITS);
    const { page = 0, size = 20, qcAgentFilter, auditStatusFilter } = payload;
    dispatch(setIsTableLoading(true));

    return axiosInstance
      .get(url, {
        params: {
          page,
          size,
          ...(qcAgentFilter?.length && { qcAgentFilter: qcAgentFilter.join(',') }),
          ...(auditStatusFilter?.length && { auditStatusFilter: auditStatusFilter.join(',') })
        },
        headers: { donotHandleError: true, includeInUptimeMetrics: true }
      })
      .then(res => {
        dispatch(setQcAgentAudits(res.data?.content || []));
        dispatch(setTotalElements(res.data?.totalElements || 0));
        dispatch(setTotalPages(res.data?.totalPages || 0));
        dispatch(setCurrentPage(page));
        dispatch(setPageSize(size));
        return res.data;
      })
      .catch(err => {
        logError('Error fetching QC team lead audits', err);
        toast.error('Failed to fetch QC team lead audits');
      })
      .finally(() => {
        dispatch(setIsTableLoading(false));
      });
  };

export const getQcAgentFilters = () => (dispatch: Dispatch) => {
  const url = getApiUrl(ApiKeys.QC_AGENT_FILTERS);

  return axiosInstance
    .get(url, {
      headers: { donotHandleError: true, includeInUptimeMetrics: true }
    })
    .then(res => {
      dispatch(setQcAgentFilters(res.data));
    })
    .catch(err => {
      logError('Error fetching QC agent filters', err);
      toast.error('Failed to fetch QC agent filters');
    });
};

export const getQcTeamLeadFilters = () => (dispatch: Dispatch) => {
  const url = getApiUrl(ApiKeys.QC_TEAM_LEAD_FILTERS);

  return axiosInstance
    .get(url, {
      headers: { donotHandleError: true, includeInUptimeMetrics: true }
    })
    .then(res => {
      dispatch(setQcTeamLeadFilters(res.data));
    })
    .catch(err => {
      logError('Error fetching QC team lead filters', err);
      toast.error('Failed to fetch QC team lead filters');
    });
};

export const getCallingAgentAudits = (page: number, size: number) => (dispatch: Dispatch) => {
  dispatch(setIsTableLoading(true));
  const url = getApiUrl(ApiKeys.CALLING_AGENT_AUDITS);

  return axiosInstance
    .get(url, {
      params: { page, size },
      headers: { donotHandleError: true, includeInUptimeMetrics: true }
    })
    .then(res => {
      dispatch(setCallingAgentAudits(res.data?.content));
    })
    .catch(err => {
      logError('Error fetching calling agent audits', err);
      toast.error('Failed to fetch calling agent audits');
    })
    .finally(() => {
      dispatch(setIsTableLoading(false));
    });
};

export const getCallingAgentAuditDetails =
  (auditAssignmentReferenceId: string) => (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.CALLING_AGENT_AUDIT_DETAILS, {
      auditAssignmentReferenceId
    });
    dispatch(setIsSidebarLoading(true));

    return axiosInstance
      .get(url, {
        headers: { donotHandleError: true, includeInUptimeMetrics: true }
      })
      .then(res => {
        dispatch(setCallingAgentAuditDetails(res.data));
      })
      .catch(err => {
        logError('Error fetching calling agent audit details', err);
        toast.error('Failed to fetch audit details');
      })
      .finally(() => {
        dispatch(setIsSidebarLoading(false));
      });
  };

export const getQcAuditDetails = (auditAssignmentReferenceId: string) => (dispatch: Dispatch) => {
  const url = getApiUrl(ApiKeys.QC_AUDIT_DETAILS, {
    auditAssignmentReferenceId
  });
  dispatch(setIsSidebarLoading(true));

  return axiosInstance
    .get(url, {
      headers: { donotHandleError: true, includeInUptimeMetrics: true }
    })
    .then(res => {
      dispatch(setCallingAgentAuditDetails(res.data));
    })
    .catch(err => {
      logError('Error fetching QC audit details', err);
      toast.error('Failed to fetch QC audit details');
    })
    .finally(() => {
      dispatch(setIsSidebarLoading(false));
    });
};

export const submitQcAgentAudit =
  (auditAssignmentReferenceId: string, payload: SubmitAuditPayload, successCallback?: () => void) =>
  (dispatch: Dispatch) => {
    dispatch(setIsFeedbackSubmitting(true));
    const url = getApiUrl(ApiKeys.SUBMIT_QC_AGENT_AUDIT, {
      auditAssignmentReferenceId
    });

    const apiPayload = {
      answers: payload.answers.map(answer => ({
        question_id: answer.questionId,
        selected_option_ids: answer.selectedOptionIds
      }))
    };

    return axiosInstance
      .put(url, apiPayload, {
        headers: { donotHandleError: true, includeInUptimeMetrics: true }
      })
      .then(res => {
        toast.success(res.data?.message || 'Audit submitted successfully');
        // Update only auditConfig, auditStatus, auditTrail in Redux
        dispatch(
          updateCallingAgentAuditDetails({
            auditConfig: res.data?.auditConfig,
            auditStatus: res.data?.auditStatus,
            auditTrail: res.data?.auditTrail
          })
        );
        if (successCallback) {
          successCallback();
        }
        return res.data;
      })
      .catch(err => {
        logError('Error submitting QC agent audit', err);
        toast.error('Failed to submit audit');
      })
      .finally(() => {
        dispatch(setIsFeedbackSubmitting(false));
      });
  };

export const submitQcTeamLeadAudit =
  (
    auditAssignmentReferenceId: string,
    payload: SubmitQcTeamLeadAuditPayload,
    successCallback?: () => void
  ) =>
  (dispatch: Dispatch) => {
    dispatch(setIsFeedbackSubmitting(true));
    const url = getApiUrl(ApiKeys.SUBMIT_QC_TEAM_LEAD_AUDIT, {
      auditAssignmentReferenceId
    });

    const apiPayload = {
      answers: payload.answers.map(answer => ({
        question_id: answer.questionId,
        selected_option_ids: answer.selectedOptionIds
      }))
    };

    return axiosInstance
      .put(url, apiPayload, {
        headers: { donotHandleError: true, includeInUptimeMetrics: true }
      })
      .then(res => {
        toast.success(res.data?.message || 'Audit submitted successfully');
        // Update only auditConfig, auditStatus, auditTrail in Redux
        dispatch(
          updateCallingAgentAuditDetails({
            auditConfig: res.data?.auditConfig,
            auditStatus: res.data?.auditStatus,
            auditTrail: res.data?.auditTrail
          })
        );
        if (successCallback) {
          successCallback();
        }
        return res.data;
      })
      .catch(err => {
        logError('Error submitting QC team lead audit', err);
        toast.error('Failed to submit audit');
      })
      .finally(() => {
        dispatch(setIsFeedbackSubmitting(false));
      });
  };
